import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  WritableSignal,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import {
  ConfirmationService,
  SelectItem,
  PrimeTemplate,
} from '@openng/optimus-ui/api';
import { firstValueFrom, forkJoin, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { CONTACT_DETAIL_CONTAINER } from 'src/app/constants/common.constants';
import { BaseContainer } from 'src/app/core/containers/base-container';
import { dynamicSort } from 'src/app/core/functions/common-functions';
import { TabService } from 'src/app/core/services/tab.service';
import { DirtyStatus } from 'src/app/model/common/dirty-status';
import { Contact } from 'src/app/model/contacts/contact';
import { ContactList } from 'src/app/model/contacts/contact-list';
import { ContactListMember } from 'src/app/model/contacts/contact-list-member';
import { AuthService } from 'src/app/services/auth.service';
import { ContactApiService } from 'src/app/services/api/contact-api.service';
import { ContactBuilderService } from 'src/app/services/builder/contact-builder.service';
import { ContactListApiService } from 'src/app/services/api/contact-list-api.service';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { ContactListBuilderService } from 'src/app/services/builder/contact-list-builder.service';
import { ContactListTableService } from 'src/app/services/table/contact-list-table.service';
import { ContactTableService } from 'src/app/services/table/contact-table.service';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { isNullOrUndefined, sortByName } from 'src/app/utils/utils';
import { Schedule } from 'src/app/model/contacts/contact-list-schedule';
import { MemberCode } from 'src/app/model/contacts/member-code';
import { CLAIMS } from 'src/app/constants/auth-constants';
import { OrderList } from '@openng/optimus-ui/orderlist';
import { AdditionalResourceListType } from 'src/app/model/contacts/additional-resource-list-type';
import { SelectChangeEvent, Select } from '@openng/optimus-ui/select';
import {
  AutoCompleteCompleteEvent,
  AutoComplete,
} from '@openng/optimus-ui/autocomplete';
import { BlockUI } from '@openng/optimus-ui/blockui';
import { Panel } from '@openng/optimus-ui/panel';
import { ProgressSpinnerComponent } from '../../../core/components/progress-spinner.component';
import { InputText } from '@openng/optimus-ui/inputtext';
import { MultiSelect } from '@openng/optimus-ui/multiselect';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { ContactListCardComponent } from '../../components/contact-list-card/contact-list-card.component';
import { ScrollPanel } from '@openng/optimus-ui/scrollpanel';
import { Dialog } from '@openng/optimus-ui/dialog';
import { MemberCodesFormComponent } from '../../components/member-codes-form/member-codes-form.component';

@Component({
  selector: 'app-contact-list-simple-view-container',
  templateUrl: './contact-list-simple-view-container.component.html',
  styleUrls: ['./contact-list-simple-view-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    BlockUI,
    Panel,
    ProgressSpinnerComponent,
    FormsModule,
    InputText,
    MultiSelect,
    Select,
    ButtonDirective,
    OrderList,
    PrimeTemplate,
    ContactListCardComponent,
    ScrollPanel,
    Dialog,
    ReactiveFormsModule,
    AutoComplete,
    MemberCodesFormComponent,
    TranslatePipe,
  ],
})
export class ContactListSimpleViewContainerComponent
  extends BaseContainer
  implements OnInit, OnDestroy
{
  protected _contactListApi = inject(ContactListApiService);
  protected _contactListTable = inject(ContactListTableService);
  protected _contactListBuilder = inject(ContactListBuilderService);
  protected _fb = inject(UntypedFormBuilder);
  protected _logAndMessage = inject(LogAndMessageService);
  protected _authApi = inject(AuthApiService);
  protected _translateService = inject(TranslateService);
  protected _contactApi = inject(ContactApiService);
  protected _contactBuilder = inject(ContactBuilderService);
  protected _contactTable = inject(ContactTableService);
  protected _confirmationService = inject(ConfirmationService);
  protected _auth = inject(AuthService);
  protected _tabService = inject(TabService);

  private lContactList: ContactList | null = null;
  @Input() set contactList(value: ContactList | null) {
    this.lContactList = value;
    if (this.lContactList) {
      this.orderableMembers = this.lContactList.members;

      // recheck auth for write access due to being embedded or standalone
      if (!this.canEdit) {
        this.canEdit = this._authApi.doesUserHaveAllClaimsFromList([
          CLAIMS.CONTACTS.CONTACT_LISTS.EDIT_CONTACT_LISTS,
        ]);
      }

      this.updateContactList();

      if (
        this._authApi.doesUserHaveAllClaimsFromList([
          CLAIMS.CONTACTS.CONTACT_LISTS.ROTATE_ALL_CONTACT_LISTS,
        ])
      ) {
        this.rotateAccess = true;
      } else {
        this._contactApi
          .getContactByEmail(this._auth.getEmail()!)
          .pipe(take(1))
          .subscribe((data: Contact) => {
            const id = data.id;
            const onList = this.lContactList!.members.some(
              (m) => m.contactId === id
            );
            const anUpdater = this.lContactList!.updaters.some(
              (u) => u.contactId === id
            );
            // Only want to update this if the user doesn't already have rotate access for some other reason
            // so this doesn't overwrite the user having directReportContactLists claim
            if (!this.rotateAccess) {
              this.rotateAccess =
                (onList &&
                  this._authApi.doesUserHaveAllClaimsFromList([
                    CLAIMS.CONTACTS.CONTACT_LISTS.ROTATE_OWN_CONTACT_LISTS,
                  ])) ||
                anUpdater;
            }
            if (!this.rotateAccess) {
              this.orderListStyle = 'contact-list-disable';
              this.dragDropEnabled = false;
            } else {
              this.orderListStyle = 'contact-list';
              this.dragDropEnabled = true;
            }
          });

        if (
          this._authApi.doesUserHaveAllClaimsFromList([
            CLAIMS.CONTACTS.CONTACT_LISTS.ROTATE_DIRECT_REPORT_CONTACT_LISTS,
          ])
        ) {
          this._contactApi
            .queryForWhoReportsToMe(this._auth.getEmail()!)
            .pipe(take(1))
            .subscribe((users) => {
              const reportingIds = users.map((report) => report.id);
              const reportOnList = this.lContactList!.members.some((m) =>
                reportingIds.includes(m.contactId)
              );
              if (reportOnList) {
                this.rotateAccess = true;
              }
              if (!this.rotateAccess) {
                this.orderListStyle = 'contact-list-disable';
                this.dragDropEnabled = false;
              } else {
                this.orderListStyle = 'contact-list';
                this.dragDropEnabled = true;
              }
            });
        }
      }

      if (!this.rotateAccess) {
        this.orderListStyle = 'contact-list-disable';
        this.dragDropEnabled = false;
      } else {
        this.orderListStyle = 'contact-list';
        this.dragDropEnabled = true;
      }
    }

    this.additionalResourceListTypeId =
      this.lContactList?.AdditionalResourceListType?.id ?? '';
    this.selectedBusinessUnits =
      this.lContactList?.businessUnits?.map((bu) => bu.id) ?? [];
  }

  readonly embedded = input(false);
  readonly contactSchedules = input<any[]>([]);

  readonly contactsUpdated = output<ContactList>();

  _orderList = viewChild<OrderList>('olist');

  get contactList() {
    return this.lContactList!;
  }

  selectedEmployees: any[] = [];
  selectedManager: any[] = [];
  addContactToListForm: UntypedFormGroup;
  orderableMembers: ContactListMember[] = [];
  contactListSchedules: Schedule[] = [];
  saving = false;
  allContacts: Contact[] = [];
  filteredContacts: SelectItem[] = [];
  displayDialog = false;
  displayMemberCodesDialog = false;
  memberCodes: MemberCode[] = [];
  name?: string;
  sub!: Subscription;
  canEdit = false;
  canEditDirectReports = false;
  canEditOwn = false;
  isAnUpdater = false;
  canAddMemberCodes = false;
  canRemoveMemberCodes = false;
  rotateAccess = false;
  orderListStyle = 'contact-list';
  dragDropEnabled = true;
  empCollapsed = false;
  mgtCollapsed = true;
  codesToDisplay: string = '';
  availableBusinessUnits: SelectItem[] = [];
  selectedBusinessUnits: string[] = [];
  additionalResourceListTypes: AdditionalResourceListType[] = [];
  additionalResourceListTypeId: string = '';
  loading: WritableSignal<boolean> = signal(true);
  addingContact: boolean = false;

  constructor() {
    super();

    this.addContactToListForm = this._fb.group({
      contact: [null, Validators.required],
    });

    // TODO: This is a temporary fix for this bug in primeng:
    // https://github.com/primefaces/primeng/issues/17557
    // Same issue in open prime: https://github.com/openng-org/open-prime/issues/609
    // once this bug is resolved we should remove this and use the built in functionality again
    effect(() => {
      const orderList = this._orderList();
      if (orderList) {
        orderList.listViewChild.onChange.subscribe((event) =>
          this.onSelectionChange(event.value)
        );
      }
    });
  }

  ngOnInit() {
    this.getAdditionalResourceListTypes();
    this.availableBusinessUnits = this._authApi
      .getUserBusinessUnits()
      .map((bu) => {
        return { label: bu.name, value: bu.id };
      });

    this.canEdit = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.CONTACTS.CONTACT_LISTS.EDIT_CONTACT_LISTS,
    ]);
    this.canAddMemberCodes = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.CONTACTS.CONTACT_LISTS.MEMBER_CODES.ADD_MEMBER_CODE,
    ]);
    this.canRemoveMemberCodes = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.CONTACTS.CONTACT_LISTS.MEMBER_CODES.REMOVE_MEMBER_CODE,
    ]);

    if (!this.contactList) {
      this.contactList = this._contactListTable.getSelected()!;
      if (!this.contactList) {
        // navigate back.
      }
    }

    this.determineCanEditDirectReports();
    this.determineCanEditOwn();
    this.determineIfUserIsAnUpdater();

    this.addContactToListForm = this._fb.group({
      contact: [null, Validators.required],
    });

    this.updateContactList();
    this.selectedManager = [];
    this.selectedEmployees = [];
  }

  ngOnDestroy(): void {
    this.allContacts = [];
    this.filteredContacts = [];
    this.orderableMembers = [];
    this.contactList = null;
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }

  updateContactList(): void {
    if (!isNullOrUndefined(this.contactList)) {
      this.memberCodes = this.contactList?.memberCodes
        ? [...this.contactList.memberCodes]
        : [];
      this.name = this.contactList?.name;
      this.displayMemberCodes();
    }
  }

  openEmployeeProfile($event: any) {
    // clear out the selected to make sure a previously viewed contact isn't used.
    this._contactTable.setSelected(null);
    this._contactTable.setScreenEmbedded(false);
    const tab = this._tabService.buildNewTab(
      CONTACT_DETAIL_CONTAINER,
      true,
      undefined,
      $event
    );
    this._tabService.openTab(tab);
  }

  contactListReorder() {
    if (!this.rotateAccess) {
      this._logAndMessage.errorAlert(
        "No permission! User doesn't have permissions to rotate this contact list, no changes have been saved."
      );
      return;
    }
    this.saving = true;
    let idx = 1;
    this.orderableMembers.forEach((m) => {
      m.order = idx++;
      m.dirtyStatus = DirtyStatus.UPDATED;
    });
    this.contactList!.members = [...this.orderableMembers];
    this.saveContactList();
  }

  findContacts() {
    const contactIdArray = [
      ...this.contactList!.members.map((m) => m.contactId),
    ];
    let contactArray = [];
    this._contactApi
      .getContacts()
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          let clone = [...response];
          const innerArray = [
            ...clone.filter((c) => contactIdArray.indexOf(c!.id) > -1),
          ];
          contactArray = [
            ...innerArray.map((c) => this._contactBuilder.buildContact(c)),
          ];
          let memberUpdates: ContactListMember[] = [];
          contactArray.forEach((c) => {
            const member = this.contactList?.members.filter(
              (m) => m.contactId === c?.id
            );
            if (member && member.length > 0) {
              const newMember = Object.assign({}, member[0]);
              newMember.contact = c;
              memberUpdates.push(Object.assign({}, newMember));
            }
          });
          this.contactList!.members = [
            ...memberUpdates.sort(dynamicSort('order', 1)),
          ];
          this.contactList = Object.assign({}, this.contactList);
        },
      });
  }

  handleARLChanges(event: SelectChangeEvent) {
    this.additionalResourceListTypeId = event.value;
    this.saveContactList();
  }

  saveContactList() {
    this.saving = true;
    const update = this._contactListBuilder.buildUpdateStatement(
      this.contactList!,
      {
        name: this.name,
        memberCodes: this.memberCodes,
        businessUnits: this.selectedBusinessUnits,
        additionalResourceListTypeId: this.additionalResourceListTypeId,
      }
    );
    this._contactListApi
      .updateContactList(this.contactList!.id, update)
      .pipe(take(1))
      .subscribe({
        next: (contactList) => {
          this.contactList = contactList;
          this.setupMemberLists();

          this.saving = false;
          if (this.addingContact) {
            this.addingContact = false;
          }
          this._logAndMessage.translateToSuccessMessage({
            bodyKey: 'COMMON.MESSAGES.SUCCESS.SAVED_SUCCESSFULLY',
            headerKey: 'COMMON.MESSAGES.HEADERS.SUBMIT',
          });
        },
        error: (error) => {
          if (this.addingContact) {
            this.addingContact = false;
          }
          this.saving = false;
          this._logAndMessage.errorLogOnly(error);
          this._logAndMessage.translateToErrorAlert({
            bodyKey: 'CONTACT.MESSAGES.ERROR.UPDATE_CONTACT_LIST',
            bodyParams: { name: this.contactList!.name },
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
          });
        },
      });
  }

  setupMemberLists() {
    this.orderableMembers = [];
    this.orderableMembers = [...this.contactList!.members];
  }

  deleteContact() {
    const today = new Date();
    let memberToRemove = null;
    if (this.selectedEmployees.length > 0) {
      memberToRemove = this.selectedEmployees[0];
    } else if (this.selectedManager.length > 0) {
      memberToRemove = this.selectedManager[0];
    }

    this._contactListApi
      .getContactListSchedules(this.contactList!.id)
      .pipe()
      .subscribe({
        next: (schedules) => {
          this.contactListSchedules = [...schedules].map((schedule) =>
            this._contactListBuilder.buildContactListSchedule(schedule)
          );
          if (this.selectedEmployees.length > 0) {
            this.selectedEmployees.forEach(async (s) => {
              const idx = this.contactList!.members.findIndex(
                (m) => m.id === s.id
              );
              let contactSchedule = this.contactListSchedules.filter(
                (f) => f.contact?.id === s.contactId
              );
              let currentSchedules = contactSchedule;
              currentSchedules = currentSchedules.filter(
                (c) => c.endTime >= today
              );
              if (currentSchedules.length > 0) {
                this._logAndMessage.translateToErrorAlert({
                  headerKey: 'COMMON.MESSAGES.HEADERS.DELETE_ERROR',
                  bodyKey: 'COMMON.MESSAGES.ERROR.DELETE_CONTACT_ERROR_MSG',
                  bodyParams: {
                    contact: s.contact.fullName,
                  },
                });
              } else {
                const translations$ = forkJoin({
                  header: this._translateService.get(
                    'CONTACT.MESSAGES.HEADERS.REMOVE_FROM_LIST'
                  ),
                  yes: this._translateService.get('COMMON.LABEL.BUTTONS.YES'),
                  no: this._translateService.get('COMMON.LABEL.BUTTONS.NO'),
                  message: this._translateService.get(
                    'CONTACT.MESSAGES.CONFIRMATION.REMOVE_FROM_LIST',
                    {
                      name: `${memberToRemove.contact.firstName} ${memberToRemove.contact.lastName}`,
                    }
                  ),
                });

                const translations = await firstValueFrom(translations$);
                this._confirmationService.confirm({
                  header: translations.header,
                  icon: 'fa fa-question-circle',
                  acceptVisible: true,
                  rejectVisible: true,
                  acceptLabel: translations.yes,
                  rejectLabel: translations.no,
                  message: translations.message,
                  accept: () => {
                    this.contactList!.members.splice(idx, 1);
                    this.selectedEmployees = [];
                    this.saveContactList();

                    if (this.selectedManager.length > 0) {
                      this.selectedManager.forEach((s) => {
                        const idx = this.contactList!.members.findIndex(
                          (m) => m.id === s.id
                        );
                        this.contactList!.members.splice(idx, 1);
                      });
                    }
                  },
                  reject: () => {
                    this.saving = false;
                  },
                });
              }
            });
          }
        },
        error: (error) => {
          console.error(error);
          this._logAndMessage.translateToErrorAlert({
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
            bodyKey: 'CONTACT.MESSAGES.ERROR.RETRIEVE_CONTACT_LISTS',
          });
        },
      });
  }

  addContact() {
    if (!this.allContacts || this.allContacts.length === 0) {
      this._contactApi
        .getContacts()
        .pipe(take(1))
        .subscribe({
          next: (response) => {
            this.allContacts = response.sort(
              dynamicSort('firstName', 1)
            ) as Contact[];
            this.filteredContacts = [
              ...this.allContacts.map((c) => ({
                label: c.firstName + ' ' + c.lastName,
                value: c.id,
              })),
            ];
            this.displayDialog = true;
          },
        });
    } else {
      this.displayDialog = true;
    }
  }

  contactSearch($event: AutoCompleteCompleteEvent) {
    if ($event.query) {
      this.filteredContacts = this.allContacts
        .filter((c) =>
          (c.firstName + ' ' + c.lastName)
            .toLowerCase()
            .includes($event.query.toLowerCase())
        )
        .filter((c: Contact) => c.inactiveAdAccount === false)
        .map((c) => ({ label: c.firstName + ' ' + c.lastName, value: c.id }));
    } else {
      this.filteredContacts = this.allContacts.map((c) => ({
        label: c.firstName + ' ' + c.lastName,
        value: c.id,
      }));
    }
  }

  addContactToList() {
    if (this.contactList!.members.length < 1) {
      this.addingContact = true;
    }

    const raw = this.addContactToListForm.getRawValue();
    const array = this.allContacts.filter((c) => c.id === raw.contact);
    if (array.length > 0) {
      this.contactList!.members = [
        ...this.contactList!.members,
        this._contactListBuilder.buildNewMember(
          this.contactList!.id,
          array[0],
          this.orderableMembers.length + 1
        ),
      ];
      this.saveContactList();
    }

    this.addContactToListForm.reset();
    this.displayDialog = false;
  }

  onSelectionChange($event: any) {
    this.selectedEmployees = $event;
  }

  manageMemberCodes() {
    this.displayMemberCodesDialog = true;
  }

  isSelected(member: ContactListMember) {
    if (this.selectedManager.length > 0) {
      return this.selectedManager.some((m) => m.id === member.id);
    }
    return false;
  }

  onMemberCodesHidden() {
    this.displayMemberCodesDialog = false;
  }

  onMemberCodesUpdated($event: MemberCode[]) {
    this.codesToDisplay = '';
    this.saving = true;
    this.displayMemberCodesDialog = false;
    this.memberCodes = $event;

    this.displayMemberCodes();
    this.saveContactList();
  }

  displayMemberCodes() {
    let assignedMemberCodes: string[] = [];
    this.memberCodes.forEach((c) => {
      assignedMemberCodes.push(c.code);
    });

    this.codesToDisplay = assignedMemberCodes.join(', ');
  }

  determineCanEditDirectReports() {
    if (
      !this._authApi.doesUserHaveAllClaimsFromList([
        CLAIMS.CONTACTS.CONTACT_LISTS.ADD_REMOVE_DIRECT_REPORTS_CONTACT_LIST,
      ])
    ) {
      return;
    }

    for (let member of this.contactList!.members) {
      if (
        this._auth
          .getUserName()
          .toLowerCase()
          .includes(member.contact?.supervisor?.toLowerCase()!)
      ) {
        this.canEditDirectReports = true;
        break;
      }
    }
  }

  determineCanEditOwn() {
    if (
      !this._authApi.doesUserHaveAllClaimsFromList([
        CLAIMS.CONTACTS.CONTACT_LISTS.ADD_REMOVE_OWN_CONTACT_LIST,
      ])
    ) {
      return;
    }

    for (let member of this.contactList!.members) {
      if (
        this._auth
          .getUserName()
          .toLowerCase()
          .includes(member.contact?.fullName?.toLowerCase()!)
      ) {
        this.canEditOwn = true;
        break;
      }
    }
  }

  determineIfUserIsAnUpdater() {
    this._contactApi
      .getContactByEmail(this._auth.getEmail()!)
      .subscribe((data: Contact) => {
        if (data) {
          for (let updater of this.contactList!.updaters) {
            if (updater.contactId === data.id) {
              this.isAnUpdater = true;
              break;
            }
          }
        }
      });
  }

  getAdditionalResourceListTypes(): void {
    this._contactListApi.getAdditionalResourceTypes().subscribe({
      next: (response: AdditionalResourceListType[]) => {
        this.additionalResourceListTypes = response;
        this.additionalResourceListTypes.sort(sortByName);
        this.loading.set(false);
      },
      error: (error) => {
        this._logAndMessage.errorLogOnly(error);
        this._logAndMessage.translateToErrorAlert({
          headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
          bodyKey: 'CONTACT.MESSAGES.ERROR.LOAD_ADDITIONAL_RESOURCE_LIST_TYPES',
        });
      },
    });
  }
}
